import { EmptyArray } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { hasPropertyRateLoyaltyUpgrade } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { canToggleAccommodationV2PropertyRateUpgrade } from 'checkout/lib/utils/loyalty/buildToggledPropertyRateItem'

export type PropertyRateUpgradeItemView =
  | App.Checkout.LEAccommodationItemView
  | App.Checkout.BedbankAccommodationItemView
  | App.Checkout.AccommodationV2AccommodationItemView

function isPropertyRateUpgradeItemView(itemView: App.Checkout.AccommodationItemView): itemView is PropertyRateUpgradeItemView {
  if (itemView.kind === 'accommodationV2') {
    // An accomV2 toggle needs both sides' rate fields and no experience bundle.
    return canToggleAccommodationV2PropertyRateUpgrade(itemView.item)
  }
  return (itemView.kind === 'le' || itemView.kind === 'bedbank') && hasPropertyRateLoyaltyUpgrade(itemView.item)
}

/**
 * Item views (LPC hotel, bedbank **and** accomV2) that carry a property-rate package upgrade, in **either** state —
 * applied (the item is the upgraded target) or removed (re-shaped back to the source). The checkout
 * member-benefit tile renders one per result and shows Apply/Remove based on `loyaltyPropertyRateUpgrade.applied`.
 *
 * Matches on upgrade *presence* (not the flash `loyaltyUpgradeAvailability` signal, which property-rate
 * upgrades never set, nor `loyaltyAppliedUpgradeType` which is cleared once removed) so the tile persists
 * through a toggle.
 */
const getCheckoutItemViewsWithPropertyRateUpgrade = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (offerViews): Array<PropertyRateUpgradeItemView> => {
    const itemViews = offerViews.data.flatMap(
      (offerView): Array<App.Checkout.AccommodationItemView> => offerView.itemViews,
    )
    const matches = itemViews.filter(isPropertyRateUpgradeItemView)

    return matches.length ? matches : EmptyArray
  },
)

export default getCheckoutItemViewsWithPropertyRateUpgrade
